import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { rgba } from 'polished';
import styled from 'styled-components';
import moment from 'moment';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import Color from '../common/Color';
import { ParagraphSmall } from '../common/Paragraph';
import ChevronRightSmallIcon from 'packages/kakapo-ui-kit/assets/icons/ChevronRightSmallIcon';
import Spinner from '../common/Spinner';
import EmptyStateCard from '../cards/EmptyStateCard';

export const Card = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlueLight};
	border-radius: 4px;
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	border-radius: 4px;
	box-shadow: 0 2px 4px ${(props) => rgba(props.theme.colors.lightBlue, 0.5)};
	cursor: pointer;

	svg {
		display: block;
	}

	&:hover {
		background: ${(props) => props.theme.colors.whiteBlue};

		&:active {
			background: ${(props) => props.theme.colors.lightBlueLight};
		}
	}
`;

export const MomentTitle = styled(Box)`
	font-weight: bold;
	font-size: 16px;
	color: ${(props) => props.theme.colors.slateBlue};
`;

function Moment(props: { moment: graphql.MomentInstance; onClick: () => void }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	return (
		<Card mt={2} p={3} onClick={props.onClick}>
			<Flex alignItems="center">
				<Box flexGrow={1} flexShrink={1}>
					<MomentTitle>{localize(props.moment.momentTemplate.title).text}</MomentTitle>
					<ParagraphSmall color="lightBlueDark">
						{moment.utc(props.moment.startDate).format('DD MMMM YYYY')} &mdash;{' '}
						{moment.utc(props.moment.endDate).format('DD MMMM YYYY')}
					</ParagraphSmall>
				</Box>
				<Flex alignItems="center" flexGrow={0} flexShrink={0}>
					<Box flexGrow={0} flexShrink={0} mr={1}>
						<Color color="primary">{t('cycle.momentsSelector.select')}</Color>
					</Box>
					<ChevronRightSmallIcon color="primary" />
				</Flex>
			</Flex>
		</Card>
	);
}

export default function CycleMomentSelector(props: {
	type: graphql.MomentType;
	onSelect: (moment?: graphql.MomentInstance, automatic?: boolean) => void;
}) {
	const { t } = useTranslation();

	const query = graphql.useCycleMomentSelectorQuery({});

	const moments = query.data?.viewer.moments.filter(
		(moment) =>
			moment.momentTemplate.type === props.type && moment.status === graphql.MomentStatus.Open,
	);

	useEffect(() => {
		if (moments && moments.length === 0) {
			props.onSelect(undefined, true);
		}
	}, [moments?.length]);

	return (
		<Box>
			{moments ? (
				moments.length > 0 ? (
					moments.map((moment) => (
						<Moment
							key={moment.id}
							moment={moment as graphql.MomentInstance}
							onClick={() => props.onSelect(moment as graphql.MomentInstance)}
						/>
					))
				) : (
					<EmptyStateCard title={t('cycle.momentsSelector.empty')} />
				)
			) : (
				<Spinner isLoading size="18px" />
			)}
		</Box>
	);
}
